import { storeToRefs } from 'pinia'
import {
  useIntegrationStore,
  type Partner
} from '@/modules/integration/stores/integrationStore'
import { helper } from '@/modules/segment/data/logicDefinitionEditorSchema'
import { capitalize } from '@/services/string'
import countries from '@/data/countries'

// Helper function to handle time unit pluralization
const formatDuration = (duration: number, unit: string) => {
  // For singular (1), remove 's' if present
  if (duration === 1) {
    return unit.endsWith('s') ? unit.slice(0, -1) : unit
  }
  // For plural, ensure unit ends with 's'
  return unit.endsWith('s') ? unit : unit + 's'
}

// Helper function to singularize a word (basic implementation)
const singularize = (word: string) => {
  if (word.endsWith('ies')) {
    return word.slice(0, -3) + 'y'
  }
  if (word.endsWith('es')) {
    return word.slice(0, -2)
  }
  if (word.endsWith('s')) {
    return word.slice(0, -1)
  }
  return word
}

type LogicItem = {
  $type: string
  value: string
  $source: string
  operator: string
  timeframe: string
  occurrence: string
}
type LogicObject = {
  and?: LogicItem[]
  or?: LogicItem[]
}

interface StringOperatorMap {
  [key: string]: string
}

const stringOperatorMap: StringOperatorMap = {}

helper.stringOperatorsField().values.forEach((v) => {
  stringOperatorMap[v.name] = v.caption
})
stringOperatorMap['equals'] = 'is'
stringOperatorMap['does not equal'] = "isn't"
stringOperatorMap['glob'] = 'matches' // instead of 'wildcard match'

const comparisonLabels: { [key: string]: string } = {
  'gte': 'at least',
  'gt': 'more than',
  'lte': 'at most',
  'lt': 'less than',
  'equals': 'exactly'
}

stringOperatorMap['is set'] = 'is set'
stringOperatorMap['is not set'] = 'is blank'
stringOperatorMap['between'] = 'between'
stringOperatorMap['before'] = 'before'
stringOperatorMap['after'] = 'after'
stringOperatorMap['include any'] = 'includes'
stringOperatorMap['do not include any'] = "doesn't include"

/**
 * Format comma-separated values nicely for display.
 * "foo, bar, baz" -> "**foo** or **bar** or **baz**"
 */
const formatMultiValue = (value: string): string => {
  if (!value) return '(please set)'
  const values = value.split(',').map(v => v.trim()).filter(v => v !== '')
  if (values.length === 0) return '(please set)'
  if (values.length === 1) return `**${values[0]}**`
  return values.map(v => `**${v}**`).join(' or ')
}

/**
 * Check if a value contains multiple comma-separated values.
 */
const hasMultipleValues = (value: string): boolean => {
  if (!value || typeof value !== 'string') return false
  const values = value.split(',').map(v => v.trim()).filter(v => v !== '')
  return values.length > 1
}

const operatorAndValue = (operator: string, value: string) => {
  if (['is set', 'is not set'].includes(operator)) {
    return stringOperatorMap[operator]
  } else if (hasMultipleValues(value)) {
    // For multi-value conditions, format nicely with "or" between values
    return `${stringOperatorMap[operator]} ${formatMultiValue(value)}`
  } else {
    return `${stringOperatorMap[operator]} **${value || '(please set)'}**`
  }
}

const queryOccurrenceMap = {}
getQueryParamOccurrenceValues().forEach((v) => {
  queryOccurrenceMap[v.name] = v.caption
})

const describeLogicObject = async (
  item: LogicItem,
  { preview }: { preview: boolean }
) => {
  const integrationStore = useIntegrationStore()
  const segmentGroupStore = useSegmentGroupStore()

  const { partners } = storeToRefs(integrationStore)
  const partner = partners.value.find((p: Partner) => p.name === item.$source)

  if (partner) {
    if (item.$type === 'isAnonymous') {
      return `Does not exist in ${partner.label}`
    }

    if (item.$type === 'isSubscriber') {
      return `Exists in ${partner.label}`
    }

    if (item.$type === 'tags') {
      if (preview) {
        return 'Is tagged'
      }
      try {
        const tags = await getIntegrationData('partner', 'tags')
        const filteredTags = tags.value
          .filter((v) => item.tagIds.some((id) => id == v.id))
          .map((v) => v.label)

        return `${
          item.operator === 'do not include any'
            ? `not ${
                partner.espFeatureSupport.tag.conditionDescription || 'tagged'
              }`
            : partner.espFeatureSupport.tag.conditionDescription || 'tagged'
        } in ${partner.label} ${filteredTags.length > 0 ? filteredTags.join(' / ') : '(please set)'}`
      } catch (error) {
        console.error('Error fetching tags:', error)
        return `(${partner.label} condition)`
      }
    }

    if (item.$type === 'customField') {
      if (partner.espFeatureSupport.customField.unstructured) {
        const fieldNameToDisplay = item.customFieldId || '(please set)'
        const customFieldLabel = (partner.espFeatureSupport.customField.label || 'custom field').toLowerCase()
        return `${partner.label} ${customFieldLabel} **${fieldNameToDisplay}** ${operatorAndValue(
          item.operator,
          item.value
        )}`
      } else {
        if (preview) {
          return 'Has a custom field value'
        }
        try {
          const customFields = await getIntegrationData(
            'partner',
            'custom_fields'
          )
          const value = customFields.value.find(
            (v) => v.id == item.customFieldId
          )
          const fieldNameToDisplay = value
            ? value.label
            : partner.espFeatureSupport.customField.create
              ? item.customFieldId
              : '(please set)'
          const customFieldLabel = (partner.espFeatureSupport.customField.label || 'custom field').toLowerCase()
          return `${partner.label} ${customFieldLabel} **${fieldNameToDisplay}** ${operatorAndValue(
            item.operator,
            item.value
          )}`
        } catch (error) {
          console.error('Error fetching custom fields:', error)
          return `(${partner.label} condition)`
        }
      }
    }

    if (item.$type === 'lists') {
      if (preview) {
        const listLabel = singularize(partner.espFeatureSupport?.list?.label?.toLowerCase() || 'list')
        return `Belongs to a ${listLabel}`
      }

      const lists = await getIntegrationData('partner', 'lists')
      const filteredLists = lists.value
        .filter((v) => item.listIds.some((id) => id == v.id))
        .map((v) => v.label)

      const listLabel = singularize(partner.espFeatureSupport?.list?.label?.toLowerCase() || 'list')
      return `${item.operator === 'do not include any' ? 'doesn\'t belong to' : 'belongs to'} ${partner.label} ${listLabel} **${
        filteredLists.length > 0 ? filteredLists.join(' / ') : '(please set)'
      }**`
    }

    if (item.$type === 'eventHistory') {
      const eventName = item.eventName || '(please set)'
      const eventLabel = partner.espFeatureSupport?.eventHistory?.label?.toLowerCase() || 'event'

      if (preview) {
        return `Has fired ${eventLabel}`
      }

      switch (item.operator) {
        case 'has_fired':
          return `has fired ${partner.label} ${eventLabel} **${eventName}**`
        case 'has_not_fired':
          return `has not fired ${partner.label} ${eventLabel} **${eventName}**`
        case 'gte':
          return `has fired ${partner.label} ${eventLabel} **${eventName}** at least **${item.count || 0}** times`
        case 'lt':
          return `has fired ${partner.label} ${eventLabel} **${eventName}** less than **${item.count || 0}** times`
        case 'equals':
          return `has fired ${partner.label} ${eventLabel} **${eventName}** exactly **${item.count || 0}** times`
        default:
          return `${partner.label} ${eventLabel} condition`
      }
    }

    if (item.$type === 'purchaseHistory') {
      const purchaseLabel = partner.espFeatureSupport?.purchaseHistory?.label?.toLowerCase() || 'purchase'

      if (preview) {
        return `Has made a ${purchaseLabel}`
      }

      // Handle "anything" operators (is set / is not set)
      if (item.operator === 'is set') {
        return `has purchased anything in ${partner.label}`
      }
      if (item.operator === 'is not set') {
        return `has not purchased anything in ${partner.label}`
      }

      // Handle specific product operators (include any / do not include any)
      const productIds = item.productIds || []
      const productList = productIds.length > 0 ? productIds.join(' / ') : '(please set)'

      switch (item.operator) {
        case 'include any':
          return `has purchased **${productList}** in ${partner.label}`
        case 'do not include any':
          return `has not purchased **${productList}** in ${partner.label}`
        default:
          return `${partner.label} ${purchaseLabel} condition`
      }
    }

    if (item.$type === 'totalRevenue') {
      const value = item.value ?? '(please set)'

      if (preview) {
        return `Total revenue in ${partner.label}`
      }

      const operatorLabels: { [key: string]: string } = {
        'gte': 'at least',
        'gt': 'greater than',
        'lte': 'at most',
        'lt': 'less than',
        'equals': 'exactly'
      }
      const operatorLabel = operatorLabels[item.operator] || item.operator

      return `total revenue in ${partner.label} is ${operatorLabel} **$${value}**`
    }
  }

  if (item.$source === 'espplaceholder') {
    if (item.$type === 'isAnonymous') {
      return `visitor is anonymous`
    }

    if (item.$type === 'isSubscriber') {
      return `visitor exists in email platform`
    }
  }

  if (item.$source === 'wordpress') {
    const [currentOrFavorite, property, taxonomyId] = item.$type.split('-', 3)
    let listOfValuesPromise
    let propertyNoun

    if (property === 'taxonomy') {
      propertyNoun = taxonomyId === 'post_tag' ? 'tag' : 'category'

      const terms = await getIntegrationData(
        'wordpress',
        propertyNoun === 'tag' ? 'tags' : 'categories'
      )

      const filteredTerms = terms.value.filter((term) =>
        item.value.some((v) => v.value == term.id)
      )

      return `${currentOrFavorite} WordPress ${propertyNoun} is ${filteredTerms
        .map((t) => t.caption)
        .join(' / ')}`
    } else if (property === 'pageId') {
      propertyNoun = 'page'

      const pages = await getIntegrationData('wordpress', 'pages')
      const filteredPages = pages.value.filter((page) =>
        item.value.some((v) => v.value == page.id)
      )

      return `${currentOrFavorite} WordPress ${propertyNoun} ${
        item.operator === 'do not include any' ? "isn't" : 'is'
      } ${filteredPages.map((page) => page.caption).join(' / ')}`
    } else if (property === 'postId') {
      propertyNoun = 'post'

      const posts = await getIntegrationData('wordpress', 'posts')
      const filteredPosts = posts.value.filter((post) =>
        item.value.some((v) => v.value == post.id)
      )

      return `${currentOrFavorite} WordPress ${propertyNoun} ${
        item.operator === 'do not include any' ? "isn't" : 'is'
      } ${filteredPosts.map((post) => post.caption).join(' / ')}`
    }
  }

  if (item.$source === 'referrer') {
    if (item.$type === 'domain') {
      const domain = item.domain || ''
      if (hasMultipleValues(domain)) {
        return `referred by ${formatMultiValue(domain)}`
      }
      return `referred by **${domain || '(please set)'}**`
    }

    if (item.$type === 'direct') {
      return `direct traffic`
    }

    if (item.$type === 'referral') {
      return `referred by any site`
    }

    if (item.$type === 'custom') {
      return `referrer ${operatorAndValue(item.operator, item.custom_referrer)}`
    }
  }

  if (item.$source === 'segments') {
    const dimension = segmentGroupStore.getById(item.$type)

    if ((item.operator || '').includes('set')) {
      return `**${dimension.name}** ${item.operator === 'is set' ? 'matches any segment' : 'matches no segment'}`
    }

    if (dimension && item.segmentId) {
      const segmentNames = dimension.segments
        .filter((s) => item.segmentId.includes(s.id))
        .map((s) => stripHtml(s.name))
      return `**${dimension.name}** ${
        item.operator === 'do not include any' ? "isn't" : 'is'
      } **${segmentNames.join(' / ') || '(please set)'}**`
    }
  }

  if (item.$source === 'rightmessage') {
    if (item.$type === 'widget') {
      const widgetStore = useWidgetStore()
      const widgets = item.value.map((id) => widgetStore.getById(id))

      return `current widget ${operatorAndValue(
        item.operator,
        widgets.map((w) => w.name).join(' / ')
      )}`
    }

    if (item.$type === 'question') {
      const questionStore = useQuestionStore()
      
      if (item.operator === 'is set') {
        return 'has answered any questions this page view'
      }
      if (item.operator === 'is not set') {
        return "hasn't answered any questions this page view"
      }
      
      if (!item.questionId || item.questionId.length === 0) {
        return `${item.operator === 'do not include any' ? "hasn't answered" : 'has answered'} **(please set)** this page view`
      }
      
      const questions = item.questionId.map((id) => questionStore.getById(id))
      const questionTexts = questions
        .filter(q => q) // Filter out any undefined questions
        .map((q) => `"${stripHtml(q.text)}"`)
        .join(' / ')

      let inclusion = `has answered **${questionTexts || '(please set)'}**`
      if (item.operator === 'do not include any') {
        inclusion = `hasn't answered **${questionTexts || '(please set)'}**`
      }

      return `${inclusion} this page view`
    }
  }

  if (item.$source === 'scores') {
    if (item.$type === 'score') {
      return `score **${item.score}** ${operatorAndValue(
        item.operator,
        item.value
      )}`
    }

    if (item.$type === 'variable') {
      return `variable **${item.variableName || '(please set)'}** ${operatorAndValue(
        item.operator,
        item.value
      )}`
    }

    if (item.$type === 'inputQuestion') {
      const questionStore = useQuestionStore()
      const question = questionStore.getById(item.questionId)
      const questionText = question ? stripHtml(question.text) : '(please set)'
      return `input question **${questionText}** ${operatorAndValue(
        item.operator,
        item.value
      )}`
    }
  }

  if (item.$source === 'location') {
    if (item.$type === 'country') {
      const matchedCountries = countries.filter((c) =>
        (item.value || []).includes(c.value)
      )
      if (matchedCountries.length) {
        const values = operatorAndValue(
          item.operator,
          matchedCountries.map((c) => c.label).join(' / ')
        )
        return `country ${values}`
      }
    }

    if (item.$type === 'city') {
      return `city ${operatorAndValue(item.operator, item.value)}`
    }
  }

  if (item.$source === 'pageviews') {
    if (item.$type === 'landingpage') {
      return `landing page ${operatorAndValue(item.operator, item.value)}`
    }

    if (item.$type === 'domain') {
      return `current domain ${operatorAndValue(item.operator, item.value)}`
    }

    if (item.$type === 'path') {
      if (item.occurrence === 'last') {
        return `current page ${operatorAndValue(item.operator, item.value)}`
      }
      
      if (item.occurrence === 'penultimate') {
        return `previous page ${operatorAndValue(item.operator, item.value)}`
      }

      const pagePhrase =
        item.operator === 'equals'
          ? item.value || '(please set)'
          : `page that ${operatorAndValue(item.operator, item.value)}`

      // Handle flexible occurrence format
      if (item.occurrence === 'any') {
        let visitDescription = ''
        
        // Build occurrence description
        if (item.occurrenceOperator === 'equals') {
          visitDescription = `visited ${pagePhrase} exactly ${item.occurrenceCount || 1} time${(item.occurrenceCount || 1) !== 1 ? 's' : ''}`
        } else if (item.occurrenceOperator === 'gte') {
          visitDescription = `visited ${pagePhrase} at least ${item.occurrenceCount || 1} time${(item.occurrenceCount || 1) !== 1 ? 's' : ''}`
        } else if (item.occurrenceOperator === 'lte') {
          visitDescription = `visited ${pagePhrase} at most ${item.occurrenceCount || 1} time${(item.occurrenceCount || 1) !== 1 ? 's' : ''}`
        } else if (item.occurrenceOperator === 'gt') {
          visitDescription = `visited ${pagePhrase} more than ${item.occurrenceCount || 1} time${(item.occurrenceCount || 1) !== 1 ? 's' : ''}`
        } else if (item.occurrenceOperator === 'lt') {
          visitDescription = `visited ${pagePhrase} less than ${item.occurrenceCount || 1} time${(item.occurrenceCount || 1) !== 1 ? 's' : ''}`
        } else {
          // Default to "at least" if operator not specified
          visitDescription = `visited ${pagePhrase}`
        }

        // Add timeframe
        if (item.timeframe === 'within') {
          visitDescription += ` in the last ${item.duration || 1} ${formatDuration(item.duration || 1, item.unit || 'day')}`
        } else if (item.timeframe === 'outside') {
          visitDescription += ` more than ${item.duration || 1} ${formatDuration(item.duration || 1, item.unit || 'day')} ago`
        }
        // For 'ever', no additional text needed

        return visitDescription
      }
      
      // Handle "has not visited" format
      if (item.occurrence === 'not_any') {
        let notVisitedDescription = `has not visited ${pagePhrase}`
        
        // Add timeframe
        if (item.timeframe === 'within') {
          notVisitedDescription += ` in the last ${item.duration || 1} ${formatDuration(item.duration || 1, item.unit || 'day')}`
        }
        // For 'ever', it means "has never visited"
        if (item.timeframe === 'ever') {
          notVisitedDescription = `has never visited ${pagePhrase}`
        }
        
        return notVisitedDescription
      }

      // Legacy format handling (for backward compatibility)
      const matches = item.occurrence?.match(/^any( (\d+))?$/)
      if (matches) {
        const num = matches[2] ? parseInt(matches[2]) : 1
        const numPhrase = num === 2 ? ' twice' : num === 3 ? ' three times' : ''
        return `visited ${pagePhrase}${numPhrase} ${
          item.timeframe === 'this 7 days' ? 'in the last 7 days' : ''
        }`
      }

      // Fallback for old format
      const num = parseInt(item.occurrence[item.occurrence.length - 1])
      const numPhrase = num === 2 ? ' twice' : num === 3 ? ' three times' : ''
      return `visited ${pagePhrase}${numPhrase} ${
        item.timeframe === 'this 7 days' ? 'in the last 7 days' : ''
      }`
    }

    if (item.$type === 'newvisitor') {
      return 'first-time visitor'
    }

    if (item.$type === 'returningvisitor') {
      if (item.timeframe === 'ever') {
        return 'has visited before'
      } else if (item.timeframe === 'within') {
        return `has visited in the last ${item.duration} ${formatDuration(item.duration, item.unit)}`
      } else if (item.timeframe === 'outside') {
        return `last visit was more than ${item.duration} ${formatDuration(item.duration, item.unit)} ago`
      }
    }

    if (item.$type === 'pageCount') {
      const operatorLabel = comparisonLabels[item.occurrenceOperator] || 'at least'
      const count = item.occurrenceCount || 1
      let description = `viewed ${operatorLabel} **${count}** page${count !== 1 ? 's' : ''}`

      if (item.scope === 'filtered' && item.value) {
        description += ` matching **${item.value}**`
      }

      if (item.timeframe === 'within') {
        description += ` in the last ${item.duration || 1} ${formatDuration(item.duration || 1, item.unit || 'day')}`
      } else if (item.timeframe === 'outside') {
        description += ` more than ${item.duration || 1} ${formatDuration(item.duration || 1, item.unit || 'day')} ago`
      }

      return description
    }

    if (item.$type === 'sessionCount') {
      const operatorLabel = comparisonLabels[item.occurrenceOperator] || 'at least'
      const count = item.occurrenceCount || 1
      return `has visited ${operatorLabel} **${count}** session${count !== 1 ? 's' : ''}`
    }

    if (item.$type === 'visitorAge') {
      const operatorLabel = comparisonLabels[item.occurrenceOperator] || 'at least'
      const duration = item.duration || 1
      const unit = formatDuration(duration, item.unit || 'day')
      return `first visited ${operatorLabel} **${duration}** ${unit} ago`
    }
  }

  if (item.$source === 'date') {
    if (item.$type === 'date') {
      if (item.operator === 'between') {
        const startDate = item.value && !isNaN(new Date(item.value).getTime()) 
          ? new Date(item.value).toLocaleDateString() 
          : '(please set)'
        const endDate = item.dateEnd && !isNaN(new Date(item.dateEnd).getTime())
          ? new Date(item.dateEnd).toLocaleDateString()
          : '(please set)'
        return `date ${operatorAndValue(
          item.operator,
          `${startDate} and ${endDate}`
        )}`
      } else {
        const startDate = item.value && !isNaN(new Date(item.value).getTime())
          ? new Date(item.value).toLocaleDateString()
          : '(please set)'
        return `date ${operatorAndValue(item.operator, startDate)}`
      }
    }

    if (item.$type === 'weekday') {
      const weekdayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
      if (!item.value || item.value.length === 0) {
        return `day of the week ${operatorAndValue(item.operator, '(please set)')}`
      }
      const weekdays = item.value
        .map((v) => weekdayNames[parseInt(v)] || v)
        .join(' / ')
      return `day of the week ${operatorAndValue(item.operator, weekdays)}`
    }

    if (item.$type === 'month') {
      if (!item.value || item.value.length === 0) {
        return `month ${operatorAndValue(item.operator, '(please set)')}`
      }
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December']
      const months = item.value
        .map((v) => monthNames[parseInt(v)] || v)
        .join(' / ')
      return `Month ${operatorAndValue(item.operator, months)}`
    }
  }

  if (item.$source === 'utm') {
    return `${item.$type} ${
      queryOccurrenceMap[item.occurrence]
    } ${operatorAndValue(item.operator, item.value)}`
  }

  if (item.$source === 'query') {
    if (item.$type === 'custom') {
      return `query parameter **${item.query || '(please set)'}** ${
        queryOccurrenceMap[item.occurrence]
      } ${operatorAndValue(item.operator, item.value)}`
    }
  }

  if (item.$source === 'device') {
    if (item.$type === 'os') {
      if (item.operator === 'is set') {
        return 'operating system is detected'
      }
      if (item.operator === 'is not set') {
        return 'operating system is not detected'
      }
      const osValues = item.value && item.value.length > 0 
        ? item.value.join(' / ')
        : '(please set)'
      return `operating system ${operatorAndValue(item.operator, osValues)}`
    }

    if (item.$type === 'browser') {
      if (item.operator === 'is set') {
        return 'browser is detected'
      }
      if (item.operator === 'is not set') {
        return 'browser is not detected'
      }
      const browserValues = item.value && item.value.length > 0
        ? item.value.join(' / ')
        : '(please set)'
      return `browser ${operatorAndValue(item.operator, browserValues)}`
    }
  }

  if (item.$source === 'jsdata') {
    if (item.$type === 'windowVariable') {
      return `javascript **window.${item.variable || '(please set)'}** ${operatorAndValue(
        item.operator,
        item.value
      )}`
    }
    if (item.$type === 'cookie') {
      return `cookie **${item.variable || '(please set)'}** ${operatorAndValue(
        item.operator,
        item.value
      )}`
    }

    if (item.$type === 'localStorage') {
      return `localStorage key **${item.variable || '(please set)'}** ${operatorAndValue(
        item.operator,
        item.value
      )}`
    }
  }

  return 'No condition specified'
}

/**
 * Describes a logic object by creating a human-readable sentence.
 * @param logic The logic object to describe.
 * @returns A promise that resolves to a string description of the logic object.
 * @throws Error if there's an issue processing the logic object.
 */
const describeLogic = async (
  logic: LogicObject,
  {
    preview,
    customFields,
    tags,
    lists
  }: { preview: boolean; customFields: any; tags: any }
): Promise<string | null> => {
  if (!logic) {
    return null
  }

  // Normalize: if the condition has {and: [...]} without an {or: [...]} wrapper,
  // wrap it so the rest of the function works uniformly.
  if (!logic.or && logic.and) {
    logic = { or: [logic] } as LogicObject
  }

  if (!logic.or || logic.or.length === 0) {
    return null
  }

  try {
    const orGroups = await Promise.all(
      logic.or.map(async (group) => {
        // Determine if this group uses AND or OR logic
        const items = group.and || group.or
        if (!items || items.length === 0) {
          return ''
        }
        const joiner = group.and ? ' and ' : ' or '

        const conditions = await Promise.all(
          items.map(async (item: LogicItem) => {
            const description = await describeLogicObject(item, {
              preview,
              customFields,
              tags,
              lists
            })
            if (!description) {
              throw new Error(
                `Failed to describe logic item: ${JSON.stringify(item)}`
              )
            }
            return description
          })
        )

        return conditions.join(joiner)
      })
    )

    const filteredOrGroups = orGroups.filter((group) => group !== '')

    if (filteredOrGroups.length === 0) {
      return null
    }

    return capitalize(filteredOrGroups.join(', or '))
  } catch (error) {
    console.error('Error in describe function:', error)
    throw error // Re-throw the error after logging
  }
}

export { describeLogicObject, describeLogic }
